import React, { useCallback, useEffect, useState } from 'react';
import { Button, Result, Space, Spin, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { adminApi } from '@services/api';
import { useAutoRefresh } from '@hooks/useAutoRefresh';
import { useLatestRequest } from '@hooks/useLatestRequest';
import { AutoRefreshToggle } from '@components/AutoRefreshToggle';
import { ActiveUsersCard } from '@components/ActiveUsersCard';
import { apiErrorMessage } from '@utils/apiError';
import type { TodayResponse } from '@/types';
import { RefreshCountdown } from './RefreshCountdown';
import { TodayBoard } from './TodayBoard';
import { istClockSeconds, istDayLabel } from './todayMath';

const { Title, Text } = Typography;

const DEFAULT_INTERVAL_MS = 60_000;

/** Why a fetch runs: only the first load and the buttons spin; background polls stay quiet. */
type FetchReason = 'load' | 'manual' | 'poll';

/** Daily health report: today's totals with pace against yesterday, polled every minute. */
export const TodayPage: React.FC = () => {
  const [data, setData] = useState<TodayResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [intervalMs, setIntervalMs] = useState(DEFAULT_INTERVAL_MS);
  // Epoch ms of the next auto refresh; null while auto refresh is off.
  const [nextRefreshAt, setNextRefreshAt] = useState<number | null>(null);
  const beginRequest = useLatestRequest();

  const fetchToday = useCallback(
    async (reason: FetchReason) => {
      const isLatest = beginRequest();
      if (reason !== 'poll') setLoading(true);
      try {
        const res = await adminApi.getToday();
        if (!isLatest()) return;
        setData(res.data);
        setFailed(null);
      } catch (err) {
        if (!isLatest()) return;
        console.error('Failed to load Today', err);
        // The last good data stays on screen; only the status line changes.
        setFailed(apiErrorMessage(err, 'Failed to load Today.'));
      } finally {
        // A poll that overtook a manual refresh ends its spinner too.
        if (isLatest()) setLoading(false);
      }
    },
    [beginRequest]
  );

  useEffect(() => {
    void fetchToday('load');
  }, [fetchToday]);

  // useAutoRefresh (re)arms its interval when these change; the countdown
  // starts from the same moment and moves on with every tick.
  useEffect(() => {
    setNextRefreshAt(autoRefresh && intervalMs > 0 ? Date.now() + intervalMs : null);
  }, [autoRefresh, intervalMs]);

  const autoTick = useCallback(() => {
    setNextRefreshAt(Date.now() + intervalMs);
    return fetchToday('poll');
  }, [fetchToday, intervalMs]);

  useAutoRefresh(autoTick, autoRefresh, intervalMs);

  if (!data) {
    if (failed) {
      return (
        <Result
          status="error"
          title="Couldn't load Today"
          subTitle={failed}
          extra={
            <Button type="primary" onClick={() => void fetchToday('manual')} loading={loading}>
              Retry
            </Button>
          }
        />
      );
    }
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Today · {istDayLabel(data.day)}{' '}
          <Text type="secondary" style={{ fontSize: 14, fontWeight: 400 }}>
            (IST, since 00:00)
          </Text>
        </Title>
        <Space size={12} wrap>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Updated {istClockSeconds(data.asOf)} IST
            {autoRefresh && nextRefreshAt !== null && (
              <>
                {' · '}
                <RefreshCountdown key={nextRefreshAt} nextAt={nextRefreshAt} />
              </>
            )}
          </Text>
          {failed && (
            <Text type="warning" style={{ fontSize: 13 }}>
              Couldn&apos;t refresh — retrying
            </Text>
          )}
          <AutoRefreshToggle
            enabled={autoRefresh}
            onEnabledChange={setAutoRefresh}
            intervalMs={intervalMs}
            onIntervalChange={setIntervalMs}
          />
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => void fetchToday('manual')}
          >
            Refresh
          </Button>
        </Space>
      </div>
      <TodayBoard data={data} />
      <div style={{ marginTop: 16 }}>
        <ActiveUsersCard autoRefresh={autoRefresh} />
      </div>
    </div>
  );
};
